import { utils, write } from "xlsx"

import type { ArtifactType } from "./artifact-types"
import type { ArtifactEntry } from "./stores/artifact-store"

import { buildPptxBlob } from "./renderers/pptx-export"
import { parseCsv } from "./renderers/sheet-data"

/**
 * Inline an artifact's embedded images as data: URIs so an exported .html file is
 * self-contained (the raw `sandbox:/mnt/data/…` refs only resolve inside the app).
 * Mirrors the sandbox preview's resolution, choosing the session- or document-
 * content endpoint from the entry's image source. Heavy deps are imported lazily
 * (like the other exporters) so this module stays light for consumers/tests.
 * Falls back to the raw content if anything fails.
 */
export async function inlineArtifactImagesForExport(
  entry: ArtifactEntry,
  content: string,
  options?: { strict?: boolean }
): Promise<string> {
  const filenames = entry.sessionFilenames ?? []
  if (filenames.length === 0) return content
  const source = entry.imageSource
  // Fall back to entry.sessionId for entries created before imageSource existed —
  // mirrors the sandbox preview so Download HTML never ships broken sandbox refs
  // that the preview happily resolved.
  const documentId = source?.kind === "document" ? source.documentId : ""
  const sessionId = source?.kind === "session" ? source.sessionId : (entry.sessionId ?? "")
  if (!documentId && !sessionId) return content
  try {
    const [
      { resolveArtifactImages },
      { fetchFileContentApi },
      { documentContentUrl },
      { BACKEND_URI },
      { SettingsService },
    ] = await Promise.all([
      import("./renderers/resolve-artifact-images"),
      import("@/api/api"),
      import("@/api/client/documents"),
      import("@/constants/config"),
      import("@/services/settings-service"),
    ])
    return await resolveArtifactImages(content, {
      sessionId,
      filenames,
      buildUrl: documentId
        ? (f) => documentContentUrl(documentId, f)
        : (f) => `${BACKEND_URI}/session-content/${sessionId}/${encodeURIComponent(f)}`,
      fetchBytes: (url) => fetchFileContentApi(url, SettingsService.getInstance().token),
      strict: options?.strict,
    })
  } catch (error) {
    if (options?.strict) throw error
    return content
  }
}

export type ArtifactExportContext = {
  entry: ArtifactEntry
  content: string
  /** Element som renderar previewn (för PNG-export via html-to-image). */
  previewElement: HTMLElement | null
  /** Legacy print bridge retained for preview compatibility; direct PDF export does not use it. */
  requestPrint: (() => void) | null
}

export type ArtifactExporter = {
  id: "html" | "pdf" | "png" | "xlsx" | "pptx"
  labelKey: string // i18n-nyckel, t.ex. "artifacts.export_pdf"
  run: (context: ArtifactExportContext) => Promise<void>
}

export type ArtifactRegistryEntry = {
  /** canvas = Fas 1-flödet (markdown). panel = ArtifactPanel med Preview/Kod. */
  previewMode: "canvas" | "panel"
  /** Språk för Kod-fliken (CodeBlock className "language-<x>"). */
  codeLanguage: string
  exporters: ArtifactExporter[]
}

export function exportFilename(title: string, extension: string): string {
  const slug = title
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
  return `${slug || "artifact"}.${extension}`
}

async function downloadBlob(blob: Blob, filename: string): Promise<void> {
  const { saveAs } = await import("file-saver")
  saveAs(blob, filename)
}

const exportHtml: ArtifactExporter = {
  id: "html",
  labelKey: "artifacts.export_html",
  run: async ({ entry, content }) => {
    const withImages = await inlineArtifactImagesForExport(entry, content)
    await downloadBlob(
      new Blob([withImages], { type: "text/html;charset=utf-8" }),
      exportFilename(entry.title, "html")
    )
  },
}

const exportPdf: ArtifactExporter = {
  id: "pdf",
  labelKey: "artifacts.export_pdf",
  run: async ({ entry, content }) => {
    if (entry.type !== "html" && entry.type !== "slides") {
      throw new Error("PDF export is only available for HTML and slides artifacts")
    }
    const withImages = await inlineArtifactImagesForExport(entry, content, { strict: true })
    const [{ exportArtifactPdfApi }, { SettingsService }] = await Promise.all([
      import("@/api/client/artifacts"),
      import("@/services/settings-service"),
    ])
    const blob = await exportArtifactPdfApi(
      { html: withImages, title: entry.title, artifactType: entry.type },
      SettingsService.getInstance().token
    )
    await downloadBlob(blob, exportFilename(entry.title, "pdf"))
  },
}

const exportPng: ArtifactExporter = {
  id: "png",
  labelKey: "artifacts.export_png",
  run: async ({ entry, previewElement }) => {
    // Throw when there is nothing to capture so the failure is visible.
    if (!previewElement) throw new Error("no preview element to capture")
    const { toPng } = await import("html-to-image")
    const dataUrl = await toPng(previewElement, { cacheBust: true, pixelRatio: 2 })
    const blob = await (await fetch(dataUrl)).blob()
    await downloadBlob(blob, exportFilename(entry.title, "png"))
  },
}

const exportXlsx: ArtifactExporter = {
  id: "xlsx",
  labelKey: "artifacts.export_xlsx",
  run: async ({ entry, content }) => {
    const { headers, rows } = parseCsv(content)
    const wb = utils.book_new()
    const ws = utils.aoa_to_sheet([headers, ...rows])
    utils.book_append_sheet(wb, ws, "Data")
    const buffer = write(wb, { bookType: "xlsx", type: "array" }) as ArrayBuffer
    await downloadBlob(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      exportFilename(entry.title, "xlsx")
    )
  },
}

const exportPptx: ArtifactExporter = {
  id: "pptx",
  labelKey: "artifacts.export_pptx",
  run: async ({ entry, content }) => {
    const blob = await buildPptxBlob(content)
    await downloadBlob(blob, exportFilename(entry.title, "pptx"))
  },
}

export const artifactRegistry: Record<ArtifactType, ArtifactRegistryEntry> = {
  markdown: { previewMode: "canvas", codeLanguage: "markdown", exporters: [] },
  html: { previewMode: "panel", codeLanguage: "html", exporters: [exportHtml, exportPdf] },
  chart: { previewMode: "panel", codeLanguage: "json", exporters: [exportPng] },
  sheet: { previewMode: "panel", codeLanguage: "csv", exporters: [exportXlsx] },
  svg: { previewMode: "panel", codeLanguage: "xml", exporters: [exportPng] },
  mermaid: { previewMode: "panel", codeLanguage: "mermaid", exporters: [exportPng] },
  react: { previewMode: "panel", codeLanguage: "tsx", exporters: [] },
  slides: { previewMode: "panel", codeLanguage: "html", exporters: [exportPptx, exportPdf] },
}

/**
 * Safe registry lookup: an entry's `type` can be an unvalidated string from a
 * persisted document. An unknown type falls back to the markdown entry (opens
 * as code, never crashes) instead of returning `undefined`.
 */
export function getArtifactRegistryEntry(type: string): ArtifactRegistryEntry {
  // Index through a string-keyed view: `type` may be an unvalidated persisted
  // value, so the lookup can be undefined at runtime even though the Record type
  // claims total coverage of ArtifactType.
  const lookup = artifactRegistry as Record<string, ArtifactRegistryEntry | undefined>
  return lookup[type] ?? artifactRegistry.markdown
}
